import { useTheme } from '@approvalmax/theme';
import { Spacing } from '@approvalmax/ui/src/components';
import { browserHelpers } from '@approvalmax/utils';
import { actions } from 'modules/common';
import { domain } from 'modules/data';
import { useDispatch } from 'modules/react-redux';
import { AcceptInvitationsStep, MobileSuccessInfo } from 'pages/auth/pages/FirstTimeSetupPage/components';
import MobileLogo from 'pages/auth/pages/FirstTimeSetupPage/components/MobileLogo/MobileLogo';
import { memo, useCallback, useMemo, useState } from 'react';
import { useMedia } from 'react-use';
import { notificationService } from 'services/notification';
import { routingService } from 'services/routing';
import { useGetInvitations, useInvalidateGetCompanies, useInvalidateGetInvitations } from 'shared/data/webApp/v2';

import { messages } from './AcceptInvitationContent.messages';
import { Body, NextButton, Root, Title } from './AcceptInvitationContent.styles';

const AcceptInvitationContent = memo(() => {
    const { data } = useGetInvitations();

    const invitations = useMemo(() => data?.items || [], [data?.items]);

    const [acceptedInvitationIds, setAcceptedInvitationIds] = useState(() =>
        invitations.map(({ companyId }) => companyId)
    );
    const [loading, setLoading] = useState(false);
    const isMobile = browserHelpers.isMobile();
    const [isMobileSuccess, setMobileSuccess] = useState(isMobile && invitations.length === 0);
    const invalidateGetInvitations = useInvalidateGetInvitations();
    const invalidateGetCompanies = useInvalidateGetCompanies();

    const dispatch = useDispatch();

    const onAcceptedInvitationsChange = useCallback((acceptedInvitationIds: string[]) => {
        setAcceptedInvitationIds(acceptedInvitationIds);
    }, []);

    const handleAccept = useCallback(async () => {
        try {
            setLoading(true);

            const acceptedIds = new Set(acceptedInvitationIds);

            await dispatch(
                actions.respondToInvitation(
                    invitations
                        .filter(({ companyId }) => acceptedIds.has(companyId))
                        .map((invitation) => ({
                            invitation,
                            response: domain.CompanyInvitationResponse.Accepted,
                        }))
                )
            );

            invalidateGetInvitations({});
            invalidateGetCompanies();

            if (isMobile) {
                setMobileSuccess(true);
            } else {
                notificationService.showInfoToast(messages.invitationAcceptedToast);

                routingService.pushToDefaultPath();
            }
        } finally {
            setLoading(false);
        }
    }, [acceptedInvitationIds, dispatch, invalidateGetCompanies, invalidateGetInvitations, invitations, isMobile]);

    const { theme } = useTheme();
    const isMobileScreen = useMedia(`(max-width: ${theme.container.breakpoint.xsmall})`);
    const title = isMobileScreen ? messages.stepInvitationsTitleMobile : messages.stepInvitationsTitle;

    if (isMobileSuccess) {
        return (
            <Root>
                <Body>
                    <MobileSuccessInfo />
                </Body>
            </Root>
        );
    }

    return (
        <Root>
            {!isMobileScreen && <Title>{title}</Title>}

            {isMobileScreen && (
                <>
                    <Spacing height={40} />

                    <MobileLogo title={title} loading={loading} />
                </>
            )}

            <Body $withPadding>
                <AcceptInvitationsStep
                    invitations={invitations}
                    acceptedInvitationIds={acceptedInvitationIds}
                    onAcceptedChange={onAcceptedInvitationsChange}
                    onNext={handleAccept}
                />

                <NextButton execute={handleAccept} disabled={loading} preset={isMobileScreen ? 'large' : 'normal'}>
                    {messages.nextButton}
                </NextButton>
            </Body>
        </Root>
    );
});

export default AcceptInvitationContent;
